import { Avatar, Box, Grid, GridProps, Stack, styled, Typography } from '@mui/material';
import { IconApproved, IconCalendar, IconClock, IconUser2 } from 'assets/img';
import {
  PopoverTwoColumns,
  PopoverTwoColumnsInfoRow,
} from 'redesign/popover-two-column-table/popover-two-columns';
import { getFullNameWithInfix } from 'hq/components/views/flexWorkers/utils';
import { DateTime } from 'luxon';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailabilityParsedItem } from '../availability/availability-calendar.types';

export const AvailabilityGridCell = ({
  availabilityBlocks,
  handleClick,
}: {
  availabilityBlocks: AvailabilityParsedItem[];
  //Likely to change as the actions for other segment types become more common
  handleClick: (...args: any[]) => void;
}) => {
  const { blockHasAvailabilitySegment, blockHasBusySegment, availabilitySegments, busySegments } =
    useMemo(() => {
      const availabilitySegments: AvailabilityParsedItem[] = [];
      const busySegments: AvailabilityParsedItem[] = [];
      for (const block of availabilityBlocks) {
        switch (block.type) {
          case 'available':
            availabilitySegments.push(block);
            break;
          case 'busy':
            busySegments.push(block);
            break;
          default:
            throw new Error(`Invalid availability type: ${block.type}`);
        }
      }
      return {
        blockHasAvailabilitySegment: availabilitySegments.length > 0,
        blockHasBusySegment: busySegments.length > 0,
        availabilitySegments,
        busySegments,
      };
    }, [availabilityBlocks]);

  return (
    <StyledContainer direction="column" spacing={3}>
      {blockHasAvailabilitySegment ? (
        <StyledAvailabilityCell
          data={availabilitySegments}
          item
          data-testid={`availability-block-${availabilitySegments[0].workerForPopover?.workerId}`}
          onClick={() =>
            handleClick(
              availabilitySegments,
              getFullNameWithInfix(availabilitySegments[0].workerForPopover),
              availabilitySegments[0].workerForPopover?.workerId
            )
          }
        />
      ) : (
        <FillerBlock />
      )}
      {blockHasBusySegment ? (
        <StyledBusyCell
          data={busySegments}
          item
          data-testid={`busy-block-${busySegments[0].workerForPopover?.workerId}`}
        />
      ) : (
        <FillerBlock />
      )}
    </StyledContainer>
  );
};

const AvailabilityCell = ({ data, ...props }: { data: AvailabilityParsedItem[] } & GridProps) => {
  const { t } = useTranslation('all-platforms-common');
  const popoverInfoRows = getPopoverInfoRows(data);
  const { popoverTitle, popoverIcon } = useMemo(() => {
    let popoverTitle: string;
    let popoverIcon: React.ReactNode;

    switch (data[0].type) {
      case 'available':
        popoverTitle = t('availability_page.worker_available');
        popoverIcon = <IconApproved width={24} height={24} />;
        break;
      case 'busy':
        popoverTitle = t('availability_page.worker_busy');
        popoverIcon = <IconClock width={24} height={24} />;
        break;
      default:
        throw new Error(`Invalid availability type: ${data[0].type}`);
    }

    return { popoverTitle, popoverIcon };
  }, [data]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const startHoverEffect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const stopHoverEffect = () => {
    closeTimer.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  return (
    <>
      <Grid
        {...props}
        onMouseEnter={startHoverEffect}
        onMouseLeave={stopHoverEffect}
        sx={{ height: '100%', width: '100%' }}
      />
      <PopoverTwoColumns
        title={popoverTitle}
        icon={popoverIcon}
        data={popoverInfoRows}
        maxWidth={350}
        maxHeight={400}
        anchorEl={anchorEl}
      />
    </>
  );
};

const StyledAvailabilityCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: 'rgba(202, 209, 248)',
  ':hover': {
    backgroundColor: 'rgba(182, 189, 228)',
    border: '1px solid',
    borderColor: theme.palette.lwPrimary?.[5],
  },
  padding: 0,
  maxHeight: '15px',
  width: '100%',
  margin: 0,
  marginTop: 15,
  marginLeft: 4,
  minHeight: '15px',
}));

const StyledBusyCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.1)',
  ':hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    border: '1px solid',
    borderColor: theme.palette.lwPrimary?.[5],
  },
  padding: 0,
  maxHeight: '15px',
  width: '100%',
  margin: 0,
  marginTop: 15,
  marginLeft: 4,
  minHeight: '15px',
}));

//Keeps the segments aligned while maintaining responsiveness
const FillerBlock = styled(Box)({
  height: '15px',
  width: '100%',
  margin: 0,
  marginTop: 15,
  marginLeft: 4,
});

const StyledContainer = styled(Stack)({
  height: '40px',
  width: '100%',
  boxSizing: 'border-box',
  flexWrap: 'nowrap',
});

export const getPopoverInfoRows = (data: AvailabilityParsedItem[]): PopoverTwoColumnsInfoRow[] => {
  const rows: PopoverTwoColumnsInfoRow[] = [];

  const startDate = DateTime.fromISO(data[0].utcStartDateTime).toLocal();

  rows.push({
    label: 'Naam',
    labelIcon: <IconUser2 />,
    value: (
      <Stack
        direction="row"
        justifyContent={'flex-end'}
        spacing={1}
        alignItems="center"
        alignContent={'center'}
        width={'100%'}
      >
        <StyledAvatarContainer>
          <Avatar src={data[0].workerForPopover?.profilePictureUrl} alt="Profile Picture" />
        </StyledAvatarContainer>
        <Typography
          variant="body1"
          fontWeight={600}
          noWrap
          maxWidth={300}
          alignSelf={'center'}
          textOverflow={'ellipsis'}
        >
          {getFullNameWithInfix(data[0].workerForPopover)}
        </Typography>
      </Stack>
    ),
  });

  rows.push({
    label: 'Datum',
    labelIcon: <IconCalendar />,
    value: startDate.toFormat('d LLL'),
  });

  const timeRanges = data.map((item) => {
    const from = DateTime.fromISO(item.utcStartDateTime).toLocal();
    const to = DateTime.fromISO(item.utcEndDateTime).toLocal();
    if (from.hour === 0 && from.minute === 0 && to.hour === 23 && to.minute === 59) {
      return `Hele dag`;
    }
    return `${from.toFormat('HH:mm')}–${to.toFormat('HH:mm')}`;
  });

  rows.push({
    label: 'Tijd',
    labelIcon: <IconClock />,
    value: (
      <Stack spacing={0.5}>
        {timeRanges.map((range, i) => (
          <Typography key={i} variant="body1" fontWeight={600}>
            {range}
          </Typography>
        ))}
      </Stack>
    ),
  });

  return rows;
};

const StyledAvatarContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));
